import { useEffect } from "react";

import seoAssets from "@/lib/seo-assets.json";

const SITE_ORIGIN = "https://invoice.polenest.com";
const DEFAULT_SEO_IMAGE = seoAssets.invoiceBuilderSocial;

type MetaName =
  | "description"
  | "robots"
  | "twitter:card"
  | "twitter:title"
  | "twitter:description"
  | "twitter:image"
  | "twitter:image:alt";
type MetaProperty =
  | "og:title"
  | "og:description"
  | "og:url"
  | "og:type"
  | "og:image"
  | "og:image:width"
  | "og:image:height"
  | "og:image:type"
  | "og:image:alt";

export interface SeoProps {
  title: string;
  description: string;
  path: string;
  robots?: "index,follow" | "noindex,follow";
  schema?: Record<string, unknown> | Record<string, unknown>[];
}

export function Seo({ title, description, path, robots = "index,follow", schema }: SeoProps) {
  useEffect(() => {
    document.title = title;
    setMetaName("description", description);
    setMetaName("robots", robots);
    setMetaName("twitter:card", "summary_large_image");
    setMetaName("twitter:title", title);
    setMetaName("twitter:description", description);
    setMetaName("twitter:image", DEFAULT_SEO_IMAGE.url);
    setMetaName("twitter:image:alt", DEFAULT_SEO_IMAGE.alt);
    setMetaProperty("og:title", title);
    setMetaProperty("og:description", description);
    setMetaProperty("og:url", canonicalUrl(path));
    setMetaProperty("og:type", "website");
    setMetaProperty("og:image", DEFAULT_SEO_IMAGE.url);
    setMetaProperty("og:image:width", String(DEFAULT_SEO_IMAGE.width));
    setMetaProperty("og:image:height", String(DEFAULT_SEO_IMAGE.height));
    setMetaProperty("og:image:type", DEFAULT_SEO_IMAGE.type);
    setMetaProperty("og:image:alt", DEFAULT_SEO_IMAGE.alt);
    setCanonical(path);
    setJsonLd(schema);

    return () => {
      document.querySelector('script[data-route-seo="json-ld"]')?.remove();
    };
  }, [description, path, robots, schema, title]);

  return null;
}

export function canonicalUrl(path: string) {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${SITE_ORIGIN}${normalizedPath}`;
}

function setMetaName(name: MetaName, content: string) {
  const selector = `meta[name="${name}"]`;
  const element = document.querySelector(selector) ?? document.createElement("meta");
  element.setAttribute("name", name);
  element.setAttribute("content", content);
  if (!element.parentElement) document.head.appendChild(element);
}

function setMetaProperty(property: MetaProperty, content: string) {
  const selector = `meta[property="${property}"]`;
  const element = document.querySelector(selector) ?? document.createElement("meta");
  element.setAttribute("property", property);
  element.setAttribute("content", content);
  if (!element.parentElement) document.head.appendChild(element);
}

function setCanonical(path: string) {
  const selector = 'link[rel="canonical"]';
  const element = document.querySelector(selector) ?? document.createElement("link");
  element.setAttribute("rel", "canonical");
  element.setAttribute("href", canonicalUrl(path));
  if (!element.parentElement) document.head.appendChild(element);
}

function setJsonLd(schema: SeoProps["schema"]) {
  document.querySelector('script[data-route-seo="json-ld"]')?.remove();
  if (!schema) return;

  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.dataset.routeSeo = "json-ld";
  script.textContent = JSON.stringify(schema);
  document.head.appendChild(script);
}
